import { Flex, Group } from '@mantine/core';
import i18n from '../../../base-dictionary';
import { TableColumn } from '../../../components/table/table';
import { toLocalDate } from '../../../helpers/timezone/timezone';
import { newCOLORS } from '../../../styles/colors';
import { secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { TruncatedText } from '../../adherence/risk-analysis/truncated-text';
import { EpicCell, InitiativeCell } from '../../tasks-table/tasks-table-cells';
import { getStoryPointsText, getTaskTypeText } from '../../tasks-table/tasks-table.helpers';
import { TaskType } from '../../tasks-table/tasks-table.type';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { TaskSortBy } from './task-detail.type';

/**
 * Returns the color associated with a specific task type.
 *
 * @param {TaskType | null} type - The type of task (Bug, Story, Task, Subtask, Spike, etc.)
 * @returns {string} A color string from the newCOLORS palette corresponding to the task type

 */
function getTypeColor(type: TaskType | null): string {
  if (!type) return newCOLORS.gray;

  switch (type) {
    case TaskType.Bug:
      return newCOLORS.red;
    case TaskType.Story:
      return newCOLORS.green;
    case TaskType.Task:
      return newCOLORS.blue;
    case TaskType.Subtask:
      return newCOLORS.purple;
    case TaskType.Spike:
      return newCOLORS.orange;
    default:
      return newCOLORS.gray;
  }
}

/**
 * Sorts an array of task records based on the specified sort criteria.
 *
 * @param {TaskRecord[]} tasks - Array of TaskRecord objects to be sorted
 * @param {TaskSortBy | null} sortBy - The sorting criteria to apply (CREATED_DATE, POINTS, TYPE, or null)
 * @returns {TaskRecord[]} A new sorted array of TaskRecord objects
 */

function sortTasks(tasks: TaskRecord[], sortBy: TaskSortBy | null): TaskRecord[] {
  if (!sortBy) return tasks;

  switch (sortBy) {
    case TaskSortBy.CREATED_DATE:
      return tasks.sort((a, b) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime());
    case TaskSortBy.POINTS:
      return tasks.sort((a, b) => (a.points ?? 0) - (b.points ?? 0));
    case TaskSortBy.TYPE:
      return tasks.sort((a, b) => (a.type ?? '').localeCompare(b.type ?? ''));
    default:
      return tasks;
  }
}

/**
 * Returns an array of column configurations for the task table.
 *
 * This function defines the structure and rendering behavior for each column
 * in the task table, including column keys, labels, sorting capabilities,
 * custom rendering functions, and width specifications.
 *
 * @returns {TableColumn<TaskRecord>[]} An array of column configuration objects
 * that specify how to display and interact with task data in a table format
 */

function getTaskTableColumns(): TableColumn<TaskRecord>[] {
  const TEXT_SIZE = 'small';
  return [
    {
      key: 'title',
      label: `${i18n.t('common.work_unit')} Key`,
      sortable: true,
      render: (_, task) => (
        <a
          href={task.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: secondaryBase }}
        >
          <Group gap={2}>
            <Text size={TEXT_SIZE}>{task.title}</Text>
            <Icon name="open_in_new" size={14} />
          </Group>
        </a>
      ),
      width: '10%',
    },
    {
      key: 'name',
      label: i18n.t('common.name'),
      sortable: true,
      render: (_, task) => (
        <TruncatedText text={task.name?.toString() || '-'} onToggle={() => {}} textSize={TEXT_SIZE} />
      ),
      width: '15%',
    },
    {
      key: 'epic',
      label: `${i18n.t('common.parent_task')} Key`,
      sortable: true,
      render: (_, task) =>
        task.epic ? (
          <a
            href={task.epic.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: secondaryBase }}
          >
            <Group gap={2}>
              <Text size={TEXT_SIZE}>{task.epic?.title?.toString() || '-'}</Text>
              <Icon name="open_in_new" size={14} />
            </Group>
          </a>
        ) : (
          <EpicCell epic={task.epic} parentWidth={100} />
        ),
      width: '8%',
    },
    {
      key: 'epic_name' as keyof TaskRecord,
      label: `${i18n.t('common.parent_task')}`,
      sortable: true,
      render: (_, task) => <EpicCell epic={task.epic} parentWidth={100} />,
      width: '10%',
    },
    {
      key: 'initiatives',
      label: i18n.t('common.initiatives'),
      sortable: true,
      render: (_, task) => <InitiativeCell initiatives={task.initiatives} parentWidth={100} />,
      width: '10%',
    },
    {
      key: 'points',
      label: i18n.t('common.estimation'),
      sortable: true,
      render: (_, task) => <Text size={TEXT_SIZE}>{getStoryPointsText(task.points)}</Text>,
      width: '8%',
    },
    {
      key: 'type',
      label: i18n.t('common.type'),
      sortable: true,
      render: (_, task) => <Text size={TEXT_SIZE}>{getTaskTypeText(task.type)}</Text>,
      width: '8%',
    },
    {
      key: 'created_date',
      label: i18n.t('common.created'),
      sortable: true,
      render: (value) => <Text size={TEXT_SIZE}>{new Date(value?.toString() || '-').toLocaleDateString() || '-'}</Text>,
      width: '8%',
    },
    {
      key: 'transitions',
      label: i18n.t('common.status_history'),
      sortable: true,
      render: (_, task) => (
        <Flex direction="column" gap={4}>
          {task.transitions?.map((transition, index) => {
            return (
              <div key={index} style={{}}>
                {`${transition.status} - ${transition.changed_by ? transition.changed_by : '-'} (${toLocalDate(
                  transition.changed_at,
                ).format('MM/DD/YYYY')})`}
              </div>
            );
          })}
        </Flex>
      ),
      width: '35%',
    },
  ];
}

function sortTaskData(data: TaskRecord[], sortBy: keyof TaskRecord, direction: 'asc' | 'desc'): TaskRecord[] {
  return [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return direction === 'asc' ? 1 : -1;
    if (bValue === null || bValue === undefined) return direction === 'asc' ? -1 : 1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });
}

export { getTaskTableColumns, getTypeColor, sortTaskData, sortTasks };
